// AdminModule.js

import React, { useState, useEffect, useMemo } from 'react';
// --- UPDATED: Import getDoc ---
import { collection, query, onSnapshot, doc, updateDoc, writeBatch, getDoc } from 'firebase/firestore'; 
import { db, functions } from '../../firebase/config';
import { httpsCallable } from 'firebase/functions';
// --- UPDATED: Import new templates and the core send function ---
import { sendNotificationEmail, userApprovedTemplate, userDetailsUpdatedTemplate } from '../../firebase/notifications';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { TrashIcon } from '../../components/icons/Icons';

export default function AdminModule({ appId }) {
    // ... (rest of AdminModule is unchanged) ...
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const handleOpenDeleteModal = (user) => {
        setUserToDelete(user);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        const userDocRef = doc(db, `/artifacts/${appId}/users`, userToDelete.id);
        try {
            await updateDoc(userDocRef, { status: "Deactivated", updatedAt: new Date() });
            alert(`Successfully deactivated user: ${userToDelete.name}`);
        } catch (error) {
            console.error("Error deactivating user:", error);
            alert(`Failed to deactivate user: ${error.message}`);
        } finally {
            setIsDeleteModalOpen(false);
            setUserToDelete(null);
        }
    };

    return (
        <div>
            <UserManagement
                db={db}
                appId={appId}
                onDeleteUser={handleOpenDeleteModal}
            />
            {isDeleteModalOpen && (
                <DeleteUserConfirmModal
                    user={userToDelete}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                />
            )}
        </div>
    );
}


function UserManagement({ db, appId, onDeleteUser }) {
    // ... (state and useEffect are unchanged) ...
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editedUsers, setEditedUsers] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

    const roles = ["User", "Approver", "Admin"];
    const departments = ['Business Development', 'Learning Design', 'Academic Affairs', 'HR', 'Admin', 'Finance', 'IT', 'Marketing', 'Teaching Operations', 'Channel Sales', 'Applications & Student Support', 'Unassigned'];
    const statuses = ["Approved", "Deactivated"];

// --- NEW: Calculate user counts ---
    const { approvedCount, deactivatedCount } = useMemo(() => {
        return users.reduce((counts, user) => {
            if (user.status === 'Approved') {
                counts.approvedCount++;
            } else if (user.status === 'Deactivated') {
                counts.deactivatedCount++;
            }
            return counts;
        }, { approvedCount: 0, deactivatedCount: 0 });
    }, [users]); // This will re-run only when the 'users' array changes

    useEffect(() => {
        if (!db || !appId) {
            setIsLoading(false);
            return;
        }
        const usersCollectionPath = `/artifacts/${appId}/users`;
        const q = query(collection(db, usersCollectionPath));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const usersData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setUsers(usersData);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [db, appId]);


    const handleLocalUpdate = (userId, field, value) => {
        setEditedUsers(prev => ({
            ...prev,
            [userId]: {
                ...users.find(u => u.id === userId),
                ...(prev[userId] || {}),
                [field]: value
            }
        }));
    };

    // --- UPDATED: handleSaveChanges now sends a notification on change ---
   const handleSaveChanges = async () => {
        const batch = writeBatch(db);
        const notificationPromises = [];
        const setUserClaims = httpsCallable(functions, 'setUserClaims');

        for (const userId of Object.keys(editedUsers)) {
            const userDocRef = doc(db, `/artifacts/${appId}/users`, userId);
            const originalUser = users.find(u => u.id === userId);
            const updatedData = editedUsers[userId];
            
            // Check if role OR department changed
            if (originalUser && (originalUser.role !== updatedData.role || originalUser.department !== updatedData.department)) {
                // 1. Send email notification (this logic is correct)
                const emailContent = userDetailsUpdatedTemplate(updatedData);
                notificationPromises.push(sendNotificationEmail({
                    to: [updatedData.email],
                    cc: ['hr@linceducation.com'],
                    ...emailContent,
                    appId
                }));
                
                // 2. Call cloud function to update claims AND Firestore
                notificationPromises.push(
                    setUserClaims({ 
                        userId: userId, 
                        role: updatedData.role, // Pass the new role
                        department: updatedData.department, // Pass the new department
                        appId: appId
                    })
                );
            } else {
                // If only status changed (or something else), just update Firestore
                const { id, ...dataToSave } = updatedData;
                batch.update(userDocRef, { ...dataToSave, updatedAt: new Date() });
            }
        }

        try {
            await batch.commit(); // Saves any minor changes
            await Promise.all(notificationPromises); // Saves major changes & sends emails
            setEditedUsers({});
            alert("Changes saved successfully!");
        } catch (e) {
            console.error("Error saving changes or setting claims:", e);
            alert("Failed to save changes.");
        }
    };
    
    // --- UPDATED: handleApproveUser now sends a notification ---
    const handleApproveUser = async (userId) => {
        const userDocRef = doc(db, `/artifacts/${appId}/users`, userId);
        try {
            await updateDoc(userDocRef, { status: "Approved", updatedAt: new Date() });
            
            // Send notification to the newly approved user
            const userSnap = await getDoc(userDocRef);
            if (userSnap.exists()) {
                const userData = userSnap.data();
                const emailContent = userApprovedTemplate(userData);
                await sendNotificationEmail({
                    to: [userData.email],
                    ...emailContent,
                    appId
                });
            }
        } catch (e) {
            console.error("Error approving user or sending notification:", e);
            alert("Failed to approve user.");
        }
    };

    /* 
    // ... (handleMakeAdmin and sorting logic are unchanged) ...
    const handleMakeAdmin = async (userToPromote) => {
        if (!window.confirm(`Are you sure you want to make ${userToPromote.name} an admin?`)) {
            return;
        }

       try {
            // Call the new 'setUserClaims' function
            const setUserClaims = httpsCallable(functions, 'setUserClaims');
            const result = await setUserClaims({ 
                userId: userToPromote.id, 
                role: "Admin", // Pass the string "Admin"
                appId: appId 
            });
            console.log(result.data.message);
        
            alert(`${userToPromote.name} is now an admin.`);

        } catch (error) {
            console.error("Error making user admin:", error);
            alert(`Failed to make admin: ${error.message}`);
        }
    };
    */
    const sortedAndFilteredUsers = useMemo(() => {
        let sortableUsers = [...users];
        if (searchTerm) {
            sortableUsers = sortableUsers.filter(user =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        sortableUsers.sort((a, b) => {
            const aValue = a[sortConfig.key] || '';
            const bValue = b[sortConfig.key] || '';
            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
        return sortableUsers;
    }, [users, searchTerm, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    if (isLoading) return <LoadingSpinner />;

    // ... (The JSX for UserManagement is unchanged) ...
    return (
        <div>
            <h2 className="text-2xl font-semibold text-[#213368] mb-4">User Management</h2>

{/* --- NEW: JSX for displaying the user counts --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow-md text-center">
                    <h3 className="text-sm font-medium text-gray-500">Approved Users</h3>
                    <p className="mt-1 text-3xl font-semibold text-green-600">{approvedCount}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-md text-center">
                    <h3 className="text-sm font-medium text-gray-500">Deactivated Users</h3>
                    <p className="mt-1 text-3xl font-semibold text-red-600">{deactivatedCount}</p>
                </div>
            </div>

            <div className="flex justify-between items-center mb-4 p-4 bg-gray-50 rounded-lg border">
                <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-1/3 p-2 border border-gray-300 rounded-lg shadow-sm"
                />
                {Object.keys(editedUsers).length > 0 && (
                    <button
                        onClick={handleSaveChanges}
                        className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700"
                    >
                        Save Changes ({Object.keys(editedUsers).length})
                    </button>
                )}
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th onClick={() => requestSort('name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Name</th>
                                <th onClick={() => requestSort('email')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Email</th>
                                <th onClick={() => requestSort('department')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Department</th>
                                <th onClick={() => requestSort('role')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Role</th>
                                <th onClick={() => requestSort('status')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedAndFilteredUsers.map(user => {
                                const currentUserData = editedUsers[user.id] || user;
                                return (
                                    <tr key={user.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{currentUserData.name}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">{currentUserData.email}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <select value={currentUserData.department} onChange={(e) => handleLocalUpdate(user.id, 'department', e.target.value)} className="p-1 border border-gray-300 rounded-md">
                                                {departments.map(d => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <select value={currentUserData.role} onChange={(e) => handleLocalUpdate(user.id, 'role', e.target.value)} className="p-1 border border-gray-300 rounded-md">
                                                {roles.map(r => <option key={r} value={r}>{r}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {currentUserData.status === 'Pending' ? (
                                                <button onClick={() => handleApproveUser(user.id)} className="px-3 py-1 text-xs bg-green-500 text-white rounded-md hover:bg-green-600">Approve</button>
                                            ) : (
                                                <select value={currentUserData.status} onChange={(e) => handleLocalUpdate(user.id, 'status', e.target.value)} className={`p-1 border border-gray-300 rounded-md ${currentUserData.status === 'Approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                            {/* make admin button removed */}
                                            <button onClick={() => onDeleteUser(user)} className="p-2 text-red-500 hover:text-red-700"><TrashIcon /></button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function DeleteUserConfirmModal({ user, onClose, onConfirm }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-red-600 mb-4">Confirm User Deactivation</h2>
                <p className="text-gray-600 mb-6">
                    Are you sure you want to deactivate the user <strong className="font-semibold">{user.name} ({user.email})</strong>? They will no longer be able to log in.
                </p>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700">
                        Confirm Deactivation
                    </button>
                </div>
            </div>
        </div>
    );
}